import { action, makeObservable, observable } from "mobx";
import { BaseModel } from "../../../../../core/util/BaseModel";
import { KanbanBoard, moveCard } from "@caldwell619/react-kanban";
import { CardProps } from "../../view/components/KanbanBoard/KanbanBoard";
import ActionsApi, { ActionsApi as IActionsApi } from "../../../../../services/api/v2/actions/Actions.api";
import { ActionField } from "../../../../../enums";
import { Panel } from "../../../../../components/ui/Panel";
import { Icon, IconSymbols } from "../../../../../components/ui/Icon";
import I18n from "../../../../../core/localization/I18n";
import { IModalContextModel } from "../../../../../core/modalZ/context/IModalContext";
import ModalContext from "../../../../../core/modalZ/context/ModalContext";
import { ButtonTypes } from "../../../../../components/ui/Button";
import PermissionsContext from "../../../../../contexts/permissions/PermissionsContext";
import { PermissionFields } from "../../../../../contexts/permissions/PermissionsTypes";

export class ActionKanbanViewModel extends BaseModel {
  data: FP.Entities.IActionSummary[];
  modalService: IModalContextModel;
  userCanEditActions: boolean;
  organisationId: number;
  projectId: number;
  actionsProvider: IActionsApi;
  authUser: FP.Entities.IUser;
  @observable isLoading = false;
  @observable board: KanbanBoard<CardProps> = { columns: [] };

  private readonly columnDefinitions = [
    { id: 1, title: "Not Started" },
    { id: 2, title: "In Progress" },
    { id: 5, title: "On Hold" },
    { id: 6, title: "Cancelled" },
    { id: 3, title: "Completed" }
  ];

  /**
   *
   */
  constructor(
    organisationId: number,
    projectId: number,
    authUser: FP.Entities.IUser,
    data: FP.Entities.IActionSummary[]
  ) {
    super();
    makeObservable(this);
    this.organisationId = organisationId;
    this.projectId = projectId;
    this.authUser = authUser;
    this.actionsProvider = ActionsApi;
    this.modalService = ModalContext;
    this.userCanEditActions = PermissionsContext.canEditField(PermissionFields.ACTIONS, organisationId, projectId);
    this.data = data;
  }

  @action
  onMount = async () => {
    this.loadActions(this.data);
  };

  onUnmount = () => {};

  @action
  setIsLoading = (isLoading: boolean) => {
    this.isLoading = isLoading;
  };

  @action
  loadActions = (actions: FP.Entities.IActionSummary[], searchText: string = "") => {
    const actionSearch = searchText.toLowerCase().trim();
    const filteredActions = actionSearch
      ? actions.filter(action => action.name?.toLowerCase().includes(actionSearch))
      : actions;
    const cardsByColumn = filteredActions.reduce((acc, action) => {
      const columnId = action.progressStatus ?? 1;

      const card: CardProps = {
        id: action.id,
        title: action.name,
        description: action.description ?? "",
        startDate: action.startDate ? new Date(action.startDate) : undefined,
        endDate: action.endDate ? new Date(action.endDate) : undefined,
        owner: action.owner,
        assignee: action.assignedToName,
        progressStatus: action.progressStatus,
        ragStatus: action.ragStatus,
        refNo: action.refNumber,
        type: action.actionTypeName,
        projectStakeholderOwners: `${action.projectStakeholderOwners}`,
        labels: [action.tags],
        noteCount: action.noteCount,
        impacts: action.impacts
      };

      if (!acc[columnId]) acc[columnId] = [];
      acc[columnId].push(card);

      return acc;
    }, {} as Record<number, CardProps[]>);

    this.board = {
      columns: this.columnDefinitions.map(({ id, title }) => ({
        id,
        title,
        cards: cardsByColumn[id] || []
      }))
    };
  };

  @action
  moveCard = async (
    card: CardProps,
    source: { fromColumnId: number; fromPosition: number },
    destination: { toColumnId: number; toPosition: number }
  ) => {
    const updatedCard: CardProps = { ...card, progressStatus: destination.toColumnId };
    const movedBoard = moveCard(this.board, source, destination);
    const updatedBoard: typeof movedBoard = {
      columns: movedBoard.columns.map(column =>
        column.id === destination.toColumnId
          ? {
              ...column,
              cards: column.cards.map(c => (c.id === card.id ? updatedCard : c))
            }
          : column
      )
    };
    this.board = updatedBoard;
    await this.actionsProvider.updateField(
      this.organisationId,
      this.projectId,
      card.id,
      "",
      destination.toColumnId,
      null as any,
      0,
      ActionField.progressStatus
    );
  };

  @action
  deleteKanbanAction = (cardId: number[], columnId: number, cardTitle: string) => {
    return new Promise<void>(resolve => {
      this.modalService.showConfirmDialog(
        <h1 className="mt-4">{I18n.t("phrases.confirm")}</h1>,
        <div className="container-fluid">
          <div className="row mb-3">
            <div className="col-12">
              <Icon symbol={IconSymbols.AlertCircle} className="mr-2" />
              {I18n.t("warnings.removeActionFromProject")}
            </div>
          </div>
          <div className="row">
            <div className="col">{I18n.t("phrases.confirmDelete", { name: cardTitle + "" })}</div>
          </div>
        </div>,
        I18n.t("phrases.yes"),
        I18n.t("phrases.no"),
        {
          wrapWidth: "small",
          spacing: "small",
          position: "middle",
          panelProps: {
            background: Panel.PanelBackgrounds.BG_WHITE
          }
        },
        async () => {
          await this.actionsProvider.deleteRange(this.organisationId, this.projectId, cardId);
          this.modalService.hide();
          await this.data;
          resolve();
        },
        () => {
          this.modalService.hide();
        },
        ButtonTypes.DANGER
      );
    });
  };
}
